/*
 * /alimony-indexation — alimony in a fixed sum, indexed by the living wage
 * (ст. 117 СК РФ). Math: alimony-ru.js on App\Services\Alimony\LivingWage data
 * (one region at a time from /api/alimony/pm/{code}.json); plumbing: ui.js.
 * What a parent needs from it, in the order they need it:
 *   1. how much the payment is now;
 *   2. how it got there — every change of the ПМ, including the falls that did
 *      NOT lower it;
 *   3. the debt for a period, month by month, minus what was paid — and the
 *      other figure some bailiffs write, so neither comes as a surprise.
 */
import * as A from './alimony-ru.js';
import * as L from './legal-ru.js';
import * as U from './ui.js';

const { $, $$, tr, esc } = U;
const today = (() => { const t = new Date(); return { y: t.getFullYear(), m: t.getMonth() + 1, d: t.getDate() }; })();
const GROUPS = ['child', 'work', 'pens'];
const S = {
    sum: NaN, k: NaN, b1: NaN, decision: '', group: 'child', region: U.CFG.region || '643',
    from: '', to: L.dateStr(today), payments: [],
};
const regions = new Map();       // code → Promise of {name, series, federal, fetched_at, last}
let data = null;
let last = null;
const T = U.tracker();

function loadRegion(code) {
    if (!regions.has(code)) {
        const p = fetch('/api/alimony/pm/' + encodeURIComponent(code) + '.json', { credentials: 'omit' })
            .then(r => { if (!r.ok) throw new Error('pm http ' + r.status); return r.json(); });
        p.catch(() => regions.delete(code));
        regions.set(code, p);
    }
    return regions.get(code);
}

async function boot() {
    readHash();
    paintInputs();
    wire();
    await pickRegion(S.region);
    U.stage('ready');
}

async function pickRegion(code) {
    S.region = code;
    $('#al-status').textContent = tr('loading');
    let d;
    try {
        d = await loadRegion(code);
    } catch (e) {
        if (S.region !== code) return;
        data = null;
        $('#al-status').textContent = tr('load_failed');
        U.diagnostic('pm_failed', { code, msg: String(e && e.message || e).slice(0, 120) });
        render();
        return;
    }
    if (S.region !== code) return;          // a later pick won
    data = d;
    $('#al-status').textContent = tr('status', { region: d.name, year: String(d.last || '').slice(0, 4), checked: d.fetched_at ? U.longDate(dateOf(d.fetched_at)) : '' });
    render();
}
const dateOf = iso => ({ y: +iso.slice(0, 4), m: +iso.slice(5, 7), d: +iso.slice(8, 10) });

function readHash() {
    const h = U.readHash();
    if (h.s || h.d) U.funnel('from_link', { pays: h.p ? h.p.split('|').length : 0 });
    const n = v => { const x = U.parseAmount(v); return Number.isFinite(x) && x > 0 ? x : NaN; };
    S.sum = n(h.s); S.k = n(h.k); S.b1 = n(h.b);
    if (L.parseDate(h.d)) S.decision = h.d;
    if (GROUPS.includes(h.g)) S.group = h.g;
    if (/^\d{3,11}$/.test(h.r || '') && $(`#al-region option[value="${h.r}"]`)) S.region = h.r;
    if (L.parseDate(h.f)) S.from = h.f;
    if (L.parseDate(h.t)) S.to = h.t;
    if (h.p) S.payments = h.p.split('|').map(x => { const [d, v] = x.split('~'); return { date: d, amount: U.parseAmount(v) }; }).filter(p => L.parseDate(p.date) && p.amount > 0);
}

function paintInputs() {
    const amt = v => (Number.isFinite(v) ? U.num(v, v % 1 ? 2 : 0) : '');
    $('#al-sum').value = amt(S.sum);
    $('#al-k').value = Number.isFinite(S.k) ? String(S.k).replace('.', ',') : '';
    $('#al-b1').value = amt(S.b1);
    $('#al-decision').value = S.decision;
    $('#al-decision').max = L.dateStr(today);
    $('#al-region').value = S.region;
    $('#al-from').value = S.from;
    $('#al-to').value = S.to;
    if (Number.isFinite(S.k) || Number.isFinite(S.b1)) $('#al-writ').open = true;
    if (S.from || S.payments.length) $('#al-debt').open = true;
    showGroup();
    paintPayments();
}

function showGroup() {
    $$('[data-group]').forEach(b => b.classList.toggle('on', b.dataset.group === S.group));
    $$('[data-group-hint]').forEach(p => { p.hidden = p.dataset.groupHint !== S.group; });
}

function paintPayments() {
    const box = $('#al-pays');
    box.innerHTML = S.payments.map((p, i) => `<div class="di-pay" data-i="${i}">
        <input class="tc-in" type="date" data-pd value="${esc(p.date)}" aria-label="${esc(tr('pay_date'))}">
        <input class="tc-in" data-pa inputmode="decimal" autocomplete="off" value="${Number.isFinite(p.amount) ? esc(U.num(p.amount, p.amount % 1 ? 2 : 0)) : ''}" aria-label="${esc(tr('pay_amount'))}" placeholder="${esc(tr('pay_amount'))}">
        <button type="button" class="tc-btn sm ghost di-x" data-rm aria-label="${esc(tr('pay_remove'))}">✕</button></div>`).join('');
    $$('[data-pa]', box).forEach(el => U.bindAmount(el, v => {
        const p = S.payments[+el.closest('.di-pay').dataset.i];
        if (!p) return;
        p.amount = v;
        T.touch('pay_amount');
        render();
    }));
}

function wire() {
    U.bindAmount($('#al-sum'), v => { S.sum = v; T.touch('sum'); render(); });
    U.bindAmount($('#al-b1'), v => { S.b1 = v; T.touch('b1'); render(); });
    $('#al-k').addEventListener('input', e => {
        const v = U.parseAmount(e.target.value);
        S.k = Number.isFinite(v) && v > 0 ? v : NaN;
        T.touch('k');
        render();
    });
    for (const [id, key] of [['#al-decision', 'decision'], ['#al-from', 'from'], ['#al-to', 'to']]) {
        $(id).addEventListener('change', e => { S[key] = e.target.value; T.touch(key); render(); });
        $(id).addEventListener('input', e => { if (L.parseDate(e.target.value)) { S[key] = e.target.value; T.touch(key); render(); } });
    }
    $$('[data-group]').forEach(b => b.addEventListener('click', () => { S.group = b.dataset.group; showGroup(); T.touch('group'); render(); U.funnel('group', { g: S.group }); }));
    $('#al-region').addEventListener('change', e => { T.touch('region'); U.funnel('region', { r: e.target.value }); pickRegion(e.target.value); });
    $('#al-add').addEventListener('click', () => {
        S.payments.push({ date: S.to || L.dateStr(today), amount: NaN });
        paintPayments();
        const rows = $$('#al-pays .di-pay');
        rows.length && $('[data-pa]', rows[rows.length - 1]).focus();
        T.touch('add_pay');
    });
    $('#al-pays').addEventListener('input', e => {
        if (!e.target.matches('[data-pd]')) return;
        const p = S.payments[+e.target.closest('.di-pay').dataset.i];
        if (!p) return;
        p.date = e.target.value;
        T.touch('pay_date');
        render();
    });
    $('#al-pays').addEventListener('click', e => {
        const b = e.target.closest('[data-rm]');
        if (!b) return;
        S.payments.splice(+b.closest('.di-pay').dataset.i, 1);
        paintPayments();
        T.touch('pay_remove');
        render();
    });
    $('#al-paste-go').addEventListener('click', () => {
        const got = parsePaymentList($('#al-paste').value);
        if (!got.length) { window.__toast && window.__toast(tr('paste_none'), 'warning'); U.funnel('paste', { n: 0 }); return; }
        S.payments = S.payments.filter(p => L.parseDate(p.date) && p.amount > 0).concat(got);
        $('#al-paste').value = '';
        $('#al-paste-box').open = false;
        paintPayments();
        T.touch('paste');
        render();
        window.__toast && window.__toast(tr('paste_ok', { n: got.length }), 'success');
        U.funnel('paste', { n: got.length });
    });
    $('#al-debt').addEventListener('toggle', () => {
        // Opening the debt section starts the period at the decision: the
        // question is almost always «сколько набежало с решения».
        if ($('#al-debt').open && !S.from && S.decision) { S.from = S.decision; $('#al-from').value = S.from; }
        render();
    });
    $('#al-copy').addEventListener('click', () => { if (last) { U.copyText(calcText()); T.action('copy', { debt: last.ledger ? 1 : 0 }); } });
    $('#al-link').addEventListener('click', () => { U.shareLink(location.href, document.title); T.action('link', {}); });
    $('#al-csv').addEventListener('click', () => {
        if (!last) return;
        const rows = last.ledger
            ? [[tr('col_month'), tr('col_due')].join(';'), ...last.ledger.months.map(m => [monthLabel(m.month), m.due.toFixed(2)].join(';'))]
            : [[tr('col_from'), tr('col_pm'), tr('col_due')].join(';'), ...last.changes.map(c => [L.dateStr(c.date), c.pm, c.due.toFixed(2)].join(';'))];
        const blob = new Blob(['\uFEFF' + rows.join('\n')], { type: 'text/csv;charset=utf-8' });
        window.__downloadFile && window.__downloadFile(blob, 'alimony-' + S.decision + '.csv');
        T.action('csv', {});
    });
    $('#al-pdf').addEventListener('click', async () => {
        if (!window.__generatePDF || !last) return;
        const r = await window.__generatePDF({ element: $('#al-report'), filename: 'alimony-' + S.decision + '.pdf', title: tr('pdf_title') });
        if (r && r.ok) T.action('pdf', {});
    });
    $('#al-live').addEventListener('click', () => {
        const a = $('#al-report .tc-panel');
        if (a) a.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        U.funnel('live_jump', {});
    });
    const drawer = el => el && el.closest('details');
    T.drawers({ writ: $('#al-writ'), debt: $('#al-debt'), changes: drawer($('#al-changes')), months: drawer($('#al-months')), rules: $('.di-rules') && $('.di-rules').closest('details'), paste: $('#al-paste-box') });
}

/** Payments pasted from a bank statement: a date and a sum per line, in any order. */
export function parsePaymentList(text) {
    const out = [];
    for (const line of String(text || '').split(/\r?\n/)) {
        const dm = /(\d{1,2})[./-](\d{1,2})[./-](\d{4})|(\d{4})-(\d{2})-(\d{2})/.exec(line);
        if (!dm) continue;
        const date = dm[4] ? `${dm[4]}-${dm[5]}-${dm[6]}` : `${dm[3]}-${dm[2].padStart(2, '0')}-${dm[1].padStart(2, '0')}`;
        if (!L.parseDate(date)) continue;
        const am = /(\d[\d\s  ]*(?:[.,]\d{1,2})?)/.exec(line.replace(dm[0], ' ').trim());
        const amount = am ? U.parseAmount(am[1]) : NaN;
        if (amount > 0) out.push({ date, amount });
    }
    return out;
}

const monthLabel = km => U.monthYear(km);
/** The living wage is set in whole rubles (a few regions use halves): no «,00». */
const pmFmt = v => U.money(v, 'RUB', v % 1 !== 0);
const isDebt = () => $('#al-debt').open;

function render() {
    const pays = S.payments.filter(p => L.parseDate(p.date) && p.amount > 0);
    U.writeHash({
        s: Number.isFinite(S.sum) ? S.sum : '', k: Number.isFinite(S.k) ? S.k : '', b: Number.isFinite(S.b1) ? S.b1 : '',
        d: S.decision, g: S.group !== 'child' ? S.group : '', r: S.region !== '643' ? S.region : '',
        f: isDebt() ? S.from : '', t: isDebt() && S.to !== L.dateStr(today) ? S.to : '',
        p: isDebt() ? pays.map(p => p.date + '~' + p.amount).join('|') : '',
    });
    if (!data) { fail('loading'); return; }
    const r = A.alimony({
        region: A.steps((data.series || {})[S.group]), federal: A.steps((data.federal || {})[S.group]),
        sum: S.sum, k: S.k, b1: S.b1, decision: S.decision,
        from: isDebt() ? S.from : '', to: isDebt() ? S.to : '', payments: pays, today: L.dateStr(today),
    });
    if (!r.ok) { fail(r.errors[0]); return; }
    last = r;
    const rub = v => U.money(v, 'RUB', true);
    const b1Label = r.b1.own ? tr('b1_own') : stepLabel(r.atDecision);
    setAnswer(rub(r.current), tr('ans_sub', { sum: rub(r.sum), date: U.numDate(L.parseDate(S.decision)) }));
    const stats = [
        { l: tr('st_k'), v: U.num(r.k, 4), s: tr(Number.isFinite(S.k) ? 'st_k_writ' : 'st_k_s', { sum: rub(r.sum), pm: pmFmt(r.b1.value) }) },
        { l: tr('st_b1'), v: pmFmt(r.b1.value), s: b1Label },
        { l: tr('st_pm'), v: r.pmNow ? pmFmt(r.pmNow.value) : '—', s: r.pmNow ? stepLabel(r.pmNow) : '' },
        { l: tr('st_growth'), v: U.signedPct((r.current / r.sum - 1) * 100, 1), s: tr('st_growth_s', { n: r.changes.filter(c => !c.kept).length }) },
    ];
    const msgs = [];
    if (r.ledger) {
        const g = r.ledger;
        stats.push({ l: tr('st_due'), v: rub(g.dueTotal), s: tr('st_due_s', { n: g.months.length, from: monthLabel(g.months[0].month), to: monthLabel(g.months[g.months.length - 1].month) }) });
        stats.push({ l: tr('st_debt'), v: rub(Math.max(0, g.debt)), s: tr('st_debt_s', { paid: rub(g.paid) }), tone: g.debt > 0.005 ? 'bad' : 'good' });
        if (g.debt < -0.005) msgs.push({ t: tr('msg_overpaid', { sum: rub(-g.debt) }) });
        if (Math.abs(g.debtAtToday - g.debt) >= 1) msgs.push({ t: tr('msg_alt', { sum: rub(Math.max(0, g.debtAtToday)) }) });
        if (g.outside) msgs.push({ t: tr('msg_outside', { n: g.outside }), tone: 'warn' });
    }
    if (r.fell) msgs.push({ t: tr('msg_fell') });
    if (r.fallback) msgs.push({ t: tr('msg_fallback') });
    if (r.approx) msgs.push({ t: tr('msg_approx'), tone: 'warn' });
    $('#al-stats').innerHTML = stats.map(s => `<div class="tc-stat ${s.tone || ''}"><div class="tc-stat-l">${esc(s.l)}</div><div class="tc-stat-v">${esc(s.v)}</div>${s.s ? `<div class="tc-stat-s">${esc(s.s)}</div>` : ''}</div>`).join('');
    $('#al-msgs').innerHTML = msgs.map(m => `<p class="tc-msg ${m.tone || ''}">${esc(m.t)}</p>`).join('');
    renderTables(r);
    $('#al-result-more').hidden = false;
    T.result(JSON.stringify([S.sum, S.k, S.b1, S.decision, S.group, S.region, isDebt(), S.from, S.to, pays.length]), {
        g: S.group, r: S.region === '643' ? 'rf' : 'region', k: Number.isFinite(S.k) ? 1 : 0, b1: Number.isFinite(S.b1) ? 1 : 0,
        debt: r.ledger ? 1 : 0, pays: pays.length, approx: r.approx ? 1 : 0, fell: r.fell ? 1 : 0, fb: r.fallback ? 1 : 0,
        yrs: Math.max(0, today.y - L.parseDate(S.decision).y),
    });
}

/** «с 01.01.2026 · Москва» / «≈ с 01.09.2019 (за II кв. 2019) · по России». */
function stepLabel(s) {
    if (!s) return '';
    const when = s.approx ? tr('since_approx', { date: U.numDate(s.date), q: s.key.replace(/^(\d{4})-Q([1-4])$/, (m, y, q) => tr('quarter', { q: ['I', 'II', 'III', 'IV'][q - 1], y })) }) : tr('since', { date: U.numDate(s.date) });
    return when + ' · ' + (s.fallback ? tr('rf_short') : data.name);
}

function fail(key) {
    last = null;
    const k = 'err_' + key;
    setAnswer('—', tr(k), true);
    if (key !== 'loading') T.invalid(k);
    $('#al-stats').innerHTML = '';
    $('#al-msgs').innerHTML = '';
    $('#al-result-more').hidden = true;
}

function setAnswer(value, sub, muted) {
    const v = $('#al-answer-v');
    v.textContent = value;
    v.classList.toggle('muted', !!muted);
    $('#al-answer-sub').textContent = sub || '';
    const lv = $('#al-live-v');
    lv.textContent = muted ? (sub || value) : value;
    lv.classList.toggle('muted', !!muted);
}

function renderTables(r) {
    const rub = v => U.money(v, 'RUB', true);
    const first = `<tr><td class="di-nowrap">${esc(U.numDate(L.parseDate(S.decision)))}</td><td class="num">${esc(pmFmt(r.b1.value))}</td><td class="num">${esc(rub(r.sum))}</td></tr>`;
    $('#al-changes').innerHTML = `<thead><tr><th>${esc(tr('col_from'))}</th><th class="num">${esc(tr('col_pm'))}</th><th class="num">${esc(tr('col_due'))}</th></tr></thead><tbody>` + first +
        r.changes.map(c => `<tr${c.kept ? ' class="al-kept"' : ''}><td class="di-nowrap">${c.approx ? '≈ ' : ''}${esc(U.numDate(c.date))}${c.fallback ? ` <span class="di-span">${esc(tr('rf_short'))}</span>` : ''}${c.kept ? `<div class="al-tag">${esc(tr('tag_kept'))}</div>` : ''}</td><td class="num">${esc(pmFmt(c.pm))}</td><td class="num">${esc(rub(c.due))}</td></tr>`).join('') + '</tbody>';
    const g = r.ledger;
    $('#al-months-wrap').hidden = !g;
    if (g) {
        $('#al-months').innerHTML = `<thead><tr><th>${esc(tr('col_month'))}</th><th class="num">${esc(tr('col_due'))}</th></tr></thead><tbody>` +
            g.months.map(m => `<tr><td>${esc(monthLabel(m.month))}</td><td class="num">${esc(rub(m.due))}</td></tr>`).join('') +
            `<tr class="di-total"><td>${esc(tr('col_total_due'))}</td><td class="num">${esc(rub(g.dueTotal))}</td></tr>` +
            `<tr><td>${esc(tr('col_paid'))}</td><td class="num">${esc(rub(g.paid))}</td></tr>` +
            `<tr class="di-total"><td>${esc(tr('col_debt'))}</td><td class="num">${esc(rub(Math.max(0, g.debt)))}</td></tr></tbody>`;
    }
}

function calcText() {
    const r = last, rub = v => U.money(v, 'RUB', true);
    const lines = [
        tr('copy_head'),
        tr('copy_base', { sum: rub(r.sum), date: U.numDate(L.parseDate(S.decision)), group: tr('group_' + S.group), region: data.name }),
        tr('copy_k', { k: r.k.toFixed(6).replace('.', ','), sum: rub(r.sum), pm: pmFmt(r.b1.value), when: r.b1.own ? tr('b1_own') : stepLabel(r.atDecision) }),
        '',
        tr('copy_changes'),
        ...r.changes.map(c => `${c.approx ? '≈ ' : ''}${U.numDate(c.date)}: ${tr('pm_word')} ${pmFmt(c.pm)}${c.fallback ? ' (' + tr('rf_short') + ')' : ''} → ${rub(c.due)}${c.kept ? ' — ' + tr('tag_kept') : ''}`),
        '',
        tr('copy_now', { sum: rub(r.current) }),
    ];
    if (r.ledger) {
        const g = r.ledger;
        lines.push('', tr('copy_debt', { from: monthLabel(g.months[0].month), to: monthLabel(g.months[g.months.length - 1].month), due: rub(g.dueTotal), paid: rub(g.paid), debt: rub(Math.max(0, g.debt)) }));
        if (Math.abs(g.debtAtToday - g.debt) >= 1) lines.push(tr('msg_alt', { sum: rub(Math.max(0, g.debtAtToday)) }));
    }
    lines.push(tr('copy_source'), location.href);
    return lines.join('\n');
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
else boot();
